import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { featureAtlas, completeFeatureChecklist, aiSuite } from '../data/features';
import { Reveal } from './MotionPrimitives';

const CHECKLIST_PREVIEW = 36;

/**
 * Unique theme per feature tab - pill, card glow, badges, bullets, chips.
 * Keys must match tab ids (ai + featureAtlas[].id).
 */
const featureThemes = {
  ai: {
    name: 'Cyan',
    pill: 'bg-gradient-to-r from-sky-400 to-cyan-300',
    pillText: 'text-black',
    card: 'from-sky-500/25 via-cyan-500/10 to-card border-cyan-400/45',
    orbA: 'bg-cyan-400/35',
    orbB: 'bg-sky-500/25',
    badge: 'bg-gradient-to-r from-sky-400 to-cyan-400',
    soft: 'bg-cyan-400/20 text-cyan-300',
    accent: 'text-cyan-300',
    chip: 'border-cyan-400/25 bg-cyan-500/10 text-cyan-50/90',
    label: 'text-cyan-300',
    highlight: 'bg-gradient-to-r from-sky-300 to-cyan-300 bg-clip-text text-transparent',
  },
  'your-business': {
    name: 'Violet',
    pill: 'bg-gradient-to-r from-violet-400 to-purple-300',
    pillText: 'text-black',
    card: 'from-violet-500/25 via-purple-500/10 to-card border-violet-400/45',
    orbA: 'bg-violet-400/35',
    orbB: 'bg-purple-500/25',
    badge: 'bg-gradient-to-r from-violet-400 to-purple-400',
    soft: 'bg-violet-400/20 text-violet-300',
    accent: 'text-violet-300',
    chip: 'border-violet-400/25 bg-violet-500/10 text-violet-50/90',
    label: 'text-violet-300',
    highlight: 'bg-gradient-to-r from-violet-300 to-fuchsia-300 bg-clip-text text-transparent',
  },
  catalog: {
    name: 'Emerald',
    pill: 'bg-gradient-to-r from-emerald-400 to-teal-300',
    pillText: 'text-black',
    card: 'from-emerald-500/25 via-teal-500/10 to-card border-emerald-400/45',
    orbA: 'bg-emerald-400/35',
    orbB: 'bg-teal-500/25',
    badge: 'bg-gradient-to-r from-emerald-400 to-teal-400',
    soft: 'bg-emerald-400/20 text-emerald-300',
    accent: 'text-emerald-300',
    chip: 'border-emerald-400/25 bg-emerald-500/10 text-emerald-50/90',
    label: 'text-emerald-300',
    highlight: 'bg-gradient-to-r from-emerald-300 to-teal-300 bg-clip-text text-transparent',
  },
  inventory: {
    name: 'Amber',
    pill: 'bg-gradient-to-r from-amber-400 to-yellow-300',
    pillText: 'text-black',
    card: 'from-amber-500/25 via-yellow-500/10 to-card border-amber-400/45',
    orbA: 'bg-amber-400/35',
    orbB: 'bg-orange-500/25',
    badge: 'bg-gradient-to-r from-amber-400 to-orange-400',
    soft: 'bg-amber-400/20 text-amber-300',
    accent: 'text-amber-300',
    chip: 'border-amber-400/25 bg-amber-500/10 text-amber-50/90',
    label: 'text-amber-300',
    highlight: 'bg-gradient-to-r from-amber-300 to-yellow-300 bg-clip-text text-transparent',
  },
  orders: {
    name: 'Rose',
    pill: 'bg-gradient-to-r from-rose-400 to-pink-300',
    pillText: 'text-black',
    card: 'from-rose-500/25 via-pink-500/10 to-card border-rose-400/45',
    orbA: 'bg-rose-400/35',
    orbB: 'bg-pink-500/25',
    badge: 'bg-gradient-to-r from-rose-400 to-pink-400',
    soft: 'bg-rose-400/20 text-rose-300',
    accent: 'text-rose-300',
    chip: 'border-rose-400/25 bg-rose-500/10 text-rose-50/90',
    label: 'text-rose-300',
    highlight: 'bg-gradient-to-r from-rose-300 to-pink-300 bg-clip-text text-transparent',
  },
  'post-order': {
    name: 'Indigo',
    pill: 'bg-gradient-to-r from-indigo-400 to-blue-300',
    pillText: 'text-black',
    card: 'from-indigo-500/25 via-blue-500/10 to-card border-indigo-400/45',
    orbA: 'bg-indigo-400/35',
    orbB: 'bg-blue-500/25',
    badge: 'bg-gradient-to-r from-indigo-400 to-blue-400',
    soft: 'bg-indigo-400/20 text-indigo-300',
    accent: 'text-indigo-300',
    chip: 'border-indigo-400/25 bg-indigo-500/10 text-indigo-50/90',
    label: 'text-indigo-300',
    highlight: 'bg-gradient-to-r from-indigo-300 to-blue-300 bg-clip-text text-transparent',
  },
  pos: {
    name: 'Lime',
    pill: 'bg-gradient-to-r from-lime-400 to-green-300',
    pillText: 'text-black',
    card: 'from-lime-500/25 via-green-500/10 to-card border-lime-400/45',
    orbA: 'bg-lime-400/35',
    orbB: 'bg-green-500/25',
    badge: 'bg-gradient-to-r from-lime-400 to-green-400',
    soft: 'bg-lime-400/20 text-lime-300',
    accent: 'text-lime-300',
    chip: 'border-lime-400/25 bg-lime-500/10 text-lime-50/90',
    label: 'text-lime-300',
    highlight: 'bg-gradient-to-r from-lime-300 to-green-300 bg-clip-text text-transparent',
  },
  promotions: {
    name: 'Fuchsia',
    pill: 'bg-gradient-to-r from-fuchsia-400 to-pink-300',
    pillText: 'text-black',
    card: 'from-fuchsia-500/25 via-pink-500/10 to-card border-fuchsia-400/45',
    orbA: 'bg-fuchsia-400/35',
    orbB: 'bg-pink-500/25',
    badge: 'bg-gradient-to-r from-fuchsia-400 to-pink-400',
    soft: 'bg-fuchsia-400/20 text-fuchsia-300',
    accent: 'text-fuchsia-300',
    chip: 'border-fuchsia-400/25 bg-fuchsia-500/10 text-fuchsia-50/90',
    label: 'text-fuchsia-300',
    highlight: 'bg-gradient-to-r from-fuchsia-300 to-pink-300 bg-clip-text text-transparent',
  },
  design: {
    name: 'Orange',
    pill: 'bg-gradient-to-r from-orange-400 to-amber-300',
    pillText: 'text-black',
    card: 'from-orange-500/25 via-amber-500/10 to-card border-orange-400/45',
    orbA: 'bg-orange-400/35',
    orbB: 'bg-amber-500/25',
    badge: 'bg-gradient-to-r from-orange-400 to-amber-400',
    soft: 'bg-orange-400/20 text-orange-300',
    accent: 'text-orange-300',
    chip: 'border-orange-400/25 bg-orange-500/10 text-orange-50/90',
    label: 'text-orange-300',
    highlight: 'bg-gradient-to-r from-orange-300 to-amber-300 bg-clip-text text-transparent',
  },
  'customer-ux': {
    name: 'Sky',
    pill: 'bg-gradient-to-r from-sky-400 to-blue-300',
    pillText: 'text-black',
    card: 'from-sky-500/25 via-blue-500/10 to-card border-sky-400/45',
    orbA: 'bg-sky-400/35',
    orbB: 'bg-blue-500/25',
    badge: 'bg-gradient-to-r from-sky-400 to-blue-400',
    soft: 'bg-sky-400/20 text-sky-300',
    accent: 'text-sky-300',
    chip: 'border-sky-400/25 bg-sky-500/10 text-sky-50/90',
    label: 'text-sky-300',
    highlight: 'bg-gradient-to-r from-sky-300 to-blue-300 bg-clip-text text-transparent',
  },
  fulfillment: {
    name: 'Teal',
    pill: 'bg-gradient-to-r from-teal-400 to-cyan-300',
    pillText: 'text-black',
    card: 'from-teal-500/25 via-cyan-500/10 to-card border-teal-400/45',
    orbA: 'bg-teal-400/35',
    orbB: 'bg-cyan-500/25',
    badge: 'bg-gradient-to-r from-teal-400 to-cyan-400',
    soft: 'bg-teal-400/20 text-teal-300',
    accent: 'text-teal-300',
    chip: 'border-teal-400/25 bg-teal-500/10 text-teal-50/90',
    label: 'text-teal-300',
    highlight: 'bg-gradient-to-r from-teal-300 to-cyan-300 bg-clip-text text-transparent',
  },
  notifications: {
    name: 'Red',
    pill: 'bg-gradient-to-r from-red-400 to-rose-300',
    pillText: 'text-black',
    card: 'from-red-500/25 via-rose-500/10 to-card border-red-400/45',
    orbA: 'bg-red-400/35',
    orbB: 'bg-rose-500/25',
    badge: 'bg-gradient-to-r from-red-400 to-rose-400',
    soft: 'bg-red-400/20 text-red-300',
    accent: 'text-red-300',
    chip: 'border-red-400/25 bg-red-500/10 text-red-50/90',
    label: 'text-red-300',
    highlight: 'bg-gradient-to-r from-red-300 to-rose-300 bg-clip-text text-transparent',
  },
  analytics: {
    name: 'Blue',
    pill: 'bg-gradient-to-r from-blue-400 to-indigo-300',
    pillText: 'text-black',
    card: 'from-blue-500/25 via-indigo-500/10 to-card border-blue-400/45',
    orbA: 'bg-blue-400/35',
    orbB: 'bg-indigo-500/25',
    badge: 'bg-gradient-to-r from-blue-400 to-indigo-400',
    soft: 'bg-blue-400/20 text-blue-300',
    accent: 'text-blue-300',
    chip: 'border-blue-400/25 bg-blue-500/10 text-blue-50/90',
    label: 'text-blue-300',
    highlight: 'bg-gradient-to-r from-blue-300 to-indigo-300 bg-clip-text text-transparent',
  },
  org: {
    name: 'Slate',
    pill: 'bg-gradient-to-r from-slate-300 to-zinc-200',
    pillText: 'text-black',
    card: 'from-slate-500/25 via-zinc-500/10 to-card border-slate-300/45',
    orbA: 'bg-slate-300/30',
    orbB: 'bg-zinc-500/25',
    badge: 'bg-gradient-to-r from-slate-400 to-zinc-400',
    soft: 'bg-slate-400/20 text-slate-200',
    accent: 'text-slate-200',
    chip: 'border-slate-400/25 bg-slate-500/10 text-slate-50/90',
    label: 'text-slate-200',
    highlight: 'bg-gradient-to-r from-slate-200 to-zinc-200 bg-clip-text text-transparent',
  },
  marketplace: {
    name: 'Yellow',
    pill: 'bg-gradient-to-r from-yellow-400 to-amber-300',
    pillText: 'text-black',
    card: 'from-yellow-500/25 via-amber-500/10 to-card border-yellow-400/45',
    orbA: 'bg-yellow-400/35',
    orbB: 'bg-amber-500/25',
    badge: 'bg-gradient-to-r from-yellow-400 to-amber-400',
    soft: 'bg-yellow-400/20 text-yellow-300',
    accent: 'text-yellow-300',
    chip: 'border-yellow-400/25 bg-yellow-500/10 text-yellow-50/90',
    label: 'text-yellow-300',
    highlight: 'bg-gradient-to-r from-yellow-300 to-amber-300 bg-clip-text text-transparent',
  },
  trust: {
    name: 'Green',
    pill: 'bg-gradient-to-r from-green-400 to-emerald-300',
    pillText: 'text-black',
    card: 'from-green-500/25 via-emerald-500/10 to-card border-green-400/45',
    orbA: 'bg-green-400/35',
    orbB: 'bg-emerald-500/25',
    badge: 'bg-gradient-to-r from-green-400 to-emerald-400',
    soft: 'bg-green-400/20 text-green-300',
    accent: 'text-green-300',
    chip: 'border-green-400/25 bg-green-500/10 text-green-50/90',
    label: 'text-green-300',
    highlight: 'bg-gradient-to-r from-green-300 to-emerald-300 bg-clip-text text-transparent',
  },
};

const fallbackTheme = featureThemes.ai;

function getTheme(id) {
  return featureThemes[id] || fallbackTheme;
}

const aiMinuteExtras = [
  'AI product description writer when listing new items',
  'Basic AI product & coupon suggestions on Standard plans',
  'Personalised AI recommendations on Premium / Custom plans',
  'Festival & seasonal demand hints for buying meetings',
  'Missing-product detection - what customers want but you do not stock',
  'Profit-pick suggestions for higher-margin shelves',
  'Weather-aware demand cues for relevant categories',
  'PDF export of AI buying recommendations for vendors',
  'Natural-language inventory questions in Zuntly Brain',
  'Expiry-aware answers for FMCG / pharma-style stock',
  'Chat history so owners continue yesterday’s AI conversation',
  'Map-based site selection with competitor density (GeoIQ)',
  'Geofence catchments and “what if we open here?” scenarios',
  'ROI modelling before signing a new shop lease',
  'Photo → script → voice → short ad video (Cinematic Studio)',
  'Ad style presets for wedding, travel, cinematic looks & festivals',
  'Ready-to-post MP4 for WhatsApp Status, Reels, and campaigns',
];

function chunkNotes(notes, size = 4) {
  const groups = [];
  for (let i = 0; i < notes.length; i += size) {
    groups.push(notes.slice(i, i + size));
  }
  return groups;
}

function FeatureMegaCard({
  badge,
  meta,
  title,
  titleHighlight,
  description,
  groups,
  chipLabel,
  chips,
  theme,
}) {
  return (
    <article
      className={`relative overflow-hidden rounded-3xl border bg-gradient-to-br p-6 shadow-glow sm:p-8 md:p-10 ${theme.card}`}
    >
      <div className={`pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full ${theme.orbA} blur-3xl`} />
      <div className={`pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full ${theme.orbB} blur-3xl`} />

      <div className="relative">
        <div className="flex flex-wrap items-center gap-3">
          <span
            className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white ${theme.badge}`}
          >
            {badge}
          </span>
          {meta && <span className="text-xs text-mute">{meta}</span>}
        </div>

        <h3 className="mt-4 text-2xl font-extrabold text-white sm:text-3xl md:text-4xl">
          {title}{' '}
          {titleHighlight && <span className={theme.highlight}>{titleHighlight}</span>}
        </h3>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-mute sm:text-base">{description}</p>

        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {groups.map((group, i) => (
            <motion.div
              key={group.id || group.name || i}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * i }}
              className="rounded-2xl border border-white/10 bg-black/40 p-5 backdrop-blur-sm"
            >
              <div className="flex items-center justify-between gap-2">
                <span
                  className={`flex h-8 w-8 items-center justify-center rounded-lg text-xs font-bold ${theme.soft}`}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                {group.role && <span className="text-[11px] text-faint">{group.role}</span>}
              </div>
              <h4 className="mt-3 text-lg font-bold text-white">{group.name}</h4>
              {group.description && (
                <p className="mt-2 text-sm leading-relaxed text-mute">{group.description}</p>
              )}
              <ul className="mt-4 space-y-1.5">
                {group.notes.map((n) => (
                  <li key={n} className="flex gap-2 text-xs text-white/70 sm:text-[13px]">
                    <span className={theme.accent}>•</span>
                    {n}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <div className="mt-8">
          <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${theme.label}`}>
            {chipLabel}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {chips.map((note) => (
              <span
                key={note}
                className={`rounded-lg border px-2.5 py-1.5 text-[11px] leading-snug sm:text-xs ${theme.chip}`}
              >
                {note}
              </span>
            ))}
          </div>
        </div>
      </div>
    </article>
  );
}

export default function FeatureAtlas() {
  const [filter, setFilter] = useState('ai');
  const [checklistOpen, setChecklistOpen] = useState(false);

  const tabs = useMemo(
    () => [
      { id: 'ai', label: 'AI for Your Shop' },
      ...featureAtlas.map((f) => ({
        id: f.id,
        label: f.tab,
      })),
    ],
    []
  );

  const allAiNotes = useMemo(() => {
    const fromSuite = aiSuite.flatMap((m) => m.notes);
    return [...fromSuite, ...aiMinuteExtras];
  }, []);

  const activeFeature = useMemo(
    () => featureAtlas.find((f) => f.id === filter),
    [filter]
  );

  const activeIndex = useMemo(
    () => featureAtlas.findIndex((f) => f.id === filter),
    [filter]
  );

  const theme = useMemo(() => getTheme(filter), [filter]);

  const featureGroups = useMemo(() => {
    if (!activeFeature) return [];
    const chunks = chunkNotes(activeFeature.notes, 4);
    return chunks.map((notes, i) => ({
      id: `${activeFeature.id}-g${i}`,
      name:
        i === 0
          ? 'What you can do'
          : i === 1
            ? 'How it helps daily'
            : i === 2
              ? 'Extra controls'
              : `More details · ${i + 1}`,
      role: `${notes.length} capabilities`,
      notes,
    }));
  }, [activeFeature]);

  return (
    <section id="features" className="section-pad relative overflow-hidden bg-black">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-accent/15 blur-[140px]" />

      <div className="relative mx-auto max-w-7xl">
        <Reveal className="mx-auto max-w-3xl text-center">
          <span className="pill-dark">Capabilities</span>
          <h2 className="mt-5 text-3xl font-extrabold tracking-tight text-white sm:text-4xl md:text-5xl">
            What you get <span className="grad-text">in Zuntly</span>
          </h2>
          <p className="mt-4 text-mute">
            Pick a tab to open that module. Every useful detail is listed for shop owners and
            distributors - nothing tucked away.
          </p>
        </Reveal>

        <Reveal className="sticky top-20 z-30 mt-10">
          <LayoutGroup>
            <div className="flex gap-2 overflow-x-auto rounded-2xl border border-white/10 bg-black/80 p-2 shadow-glow backdrop-blur-xl scrollbar-none">
              {tabs.map((tab) => {
                const active = filter === tab.id;
                const tabTheme = getTheme(tab.id);
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setFilter(tab.id)}
                    className={`relative shrink-0 rounded-full px-4 py-2 text-xs font-semibold transition sm:text-sm ${
                      active ? tabTheme.pillText : 'text-mute hover:text-white'
                    }`}
                  >
                    {active && (
                      <motion.span
                        layoutId="feature-pill"
                        className={`absolute inset-0 rounded-full ${tabTheme.pill}`}
                        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                      />
                    )}
                    <span className="relative z-10">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </LayoutGroup>
        </Reveal>

        <AnimatePresence mode="wait">
          <motion.div
            key={filter}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35 }}
            className="mt-8"
          >
            {filter === 'ai' ? (
              <FeatureMegaCard
                badge="AI in Zuntly"
                meta={`${aiSuite.length} helpers · ${allAiNotes.length}+ capabilities`}
                title="AI Features that help you"
                titleHighlight="buy, stock & advertise"
                description="Everything AI-powered inside Zuntly - explained for shop owners and distributors. Four helpers in your control panel, plus every small AI touch used across products, offers, and expansion."
                groups={aiSuite.map((m) => ({
                  id: m.id,
                  name: m.name,
                  role: m.role,
                  description: m.description,
                  notes: m.notes,
                }))}
                chipLabel="Every AI minute detail"
                chips={allAiNotes}
                theme={theme}
              />
            ) : (
              activeFeature && (
                <FeatureMegaCard
                  badge={`Module ${String(activeIndex + 1).padStart(2, '0')}`}
                  meta={`${activeFeature.notes.length} capabilities`}
                  title={activeFeature.title}
                  titleHighlight={null}
                  description={[activeFeature.summary, activeFeature.detail]
                    .filter(Boolean)
                    .join(' ')}
                  groups={featureGroups}
                  chipLabel="Every minute detail in this module"
                  chips={activeFeature.notes}
                  theme={theme}
                />
              )
            )}
          </motion.div>
        </AnimatePresence>

        <Reveal className="mt-16">
          <div className="rounded-3xl border border-white/10 bg-card/80 p-6 sm:p-8">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
              Complete inventory
            </p>
            <h3 className="mt-2 text-2xl font-bold text-white">
              {completeFeatureChecklist.length}+ things included
            </h3>

            <div className="mt-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {completeFeatureChecklist.slice(0, CHECKLIST_PREVIEW).map((item, i) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-20px' }}
                  transition={{ delay: Math.min(i * 0.015, 0.3) }}
                  whileHover={{ scale: 1.02, borderColor: 'rgba(56,189,248,0.45)' }}
                  className="flex gap-2 rounded-xl border border-white/10 bg-black/50 px-3 py-2.5 text-sm text-mute"
                >
                  <span className="font-mono text-[10px] font-bold text-accent tabular-nums">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span>{item}</span>
                </motion.div>
              ))}
            </div>

            <AnimatePresence initial={false}>
              {checklistOpen && (
                <motion.div
                  key="checklist-more"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden"
                >
                  <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {completeFeatureChecklist.slice(CHECKLIST_PREVIEW).map((item, i) => {
                      const index = CHECKLIST_PREVIEW + i;
                      return (
                        <motion.div
                          key={item}
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            delay: Math.min(i * 0.02, 0.4),
                            duration: 0.35,
                            ease: [0.16, 1, 0.3, 1],
                          }}
                          whileHover={{ scale: 1.02, borderColor: 'rgba(56,189,248,0.45)' }}
                          className="flex gap-2 rounded-xl border border-white/10 bg-black/50 px-3 py-2.5 text-sm text-mute"
                        >
                          <span className="font-mono text-[10px] font-bold text-accent tabular-nums">
                            {String(index + 1).padStart(2, '0')}
                          </span>
                          <span>{item}</span>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {completeFeatureChecklist.length > CHECKLIST_PREVIEW && (
              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={() => setChecklistOpen((v) => !v)}
                  className="btn-ghost-sm inline-flex items-center gap-2"
                  aria-expanded={checklistOpen}
                >
                  {checklistOpen
                    ? 'Show less'
                    : `Show more (${completeFeatureChecklist.length - CHECKLIST_PREVIEW})`}
                  <motion.span
                    animate={{ rotate: checklistOpen ? 180 : 0 }}
                    transition={{ duration: 0.25 }}
                    className="text-xs"
                  >
                    ▼
                  </motion.span>
                </button>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
